import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  MenuItem,
  ListItemText,
  Box,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
} from "@mui/material";
import { getDepartmentsByLocation, getEmployeeMusterRoll, getEmployeeProduction } from "src/api/faceRegistration";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { getURL } from "src/utils/config";
import axios from "axios";
import Page from "src/components/Page";
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';

const EMPTY_CELL_VALUE = "N/A";
const OUTLINED_BORDER_COLOR = "rgba(0, 0, 0, 0.23)";
const FILTER_CONTROL_WIDTH = 220;
const INITIAL_VISIBLE_ROWS = 20;
const ROW_LOAD_BATCH_SIZE = 20;
const SCROLL_LOAD_THRESHOLD_PX = 80;
const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

const normalizeDepartmentOptions = (departments) =>
  (Array.isArray(departments) ? departments : (departments ? [departments] : []))
    .map((departmentItem, index) => ({
      ...departmentItem,
      Id: departmentItem?.Id ??
        departmentItem?.ID ??
        departmentItem?.id ??
        departmentItem?.DepartmentId ??
        departmentItem?.DepartmentID ??
        departmentItem?.department_id ??
        `department-${index}`,
      DepartmentName: String(
        departmentItem?.DepartmentName ??
        departmentItem?.departmentName ??
        departmentItem?.Department ??
        departmentItem?.department ??
        departmentItem?.Name ??
        departmentItem?.name ??
        ''
      ).trim(),
    }))
    .filter((departmentItem) => departmentItem.DepartmentName);

// Mock API for absent days
const mockAbsentDaysAPI = (employeeName) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const mockData = [
        {
          employeeName: employeeName,
          location: "Location 1",
          departmentName: "Production",
          leaveType: "Sick Leave",
          date: "2026-01-15"
        },
        {
          employeeName: employeeName,
          location: "Location 1",
          departmentName: "Production",
          leaveType: "Casual Leave",
          date: "2026-01-20"
        },
        {
          employeeName: employeeName,
          location: "Location 1",
          departmentName: "Production",
          leaveType: "Annual Leave",
          date: "2026-01-25"
        },
        {
          employeeName: employeeName,
          location: "Location 1",
          departmentName: "Production",
          leaveType: "Sick Leave",
          date: "2026-02-05"
        },
        {
          employeeName: employeeName,
          location: "Location 1",
          departmentName: "Production",
          leaveType: "Casual Leave",
          date: "2026-02-10"
        }
      ];
      resolve(mockData);
    }, 500);
  });
};

const getAttendanceItems = (item) => {
  const attendance = item?.Attendance ?? item?.attendance ?? item?.attendance_details ?? [];

  if (Array.isArray(attendance)) {
    return attendance;
  }

  if (typeof attendance !== "string" || attendance.trim() === "") {
    return [];
  }

  try {
    const parsedAttendance = JSON.parse(attendance);
    return Array.isArray(parsedAttendance) ? parsedAttendance : [];
  } catch (error) {
    return [];
  }
};

const getAttendanceDate = (attendanceItem) => getFirstFieldValue(attendanceItem, [
  "Date",
  "date",
  "AttendanceDate",
  "attendanceDate",
  "attendance_date"
], "");

const getAttendanceStatus = (attendanceItem) => getFirstFieldValue(attendanceItem, [
  "Status",
  "status",
  "AttendanceStatus",
  "attendanceStatus",
  "attendance_status"
], "-");

const formatAttendanceDateLabel = (date) => {
  if (!date) {
    return "";
  }

  const parsedDate = dayjs(date);
  return parsedDate.isValid() ? parsedDate.format("D-MMM") : date;
};

const getSelectedMonthLabel = (selectedMonth) => {
  if (!selectedMonth) {
    return EMPTY_CELL_VALUE;
  }

  if (typeof selectedMonth === "object") {
    const monthValue = selectedMonth.value;
    return selectedMonth.label || MONTH_LABELS[Number(monthValue) - 1] || String(monthValue || EMPTY_CELL_VALUE);
  }

  const numericMonth = Number(selectedMonth);

  if (Number.isInteger(numericMonth) && numericMonth >= 1 && numericMonth <= 12) {
    return MONTH_LABELS[numericMonth - 1];
  }

  return String(selectedMonth);
};

const generateAttendanceColumns = (rows) => {
  const attendanceDates = [];

  (Array.isArray(rows) ? rows : []).forEach((item) => {
    getAttendanceItems(item).forEach((attendanceItem) => {
      const date = getAttendanceDate(attendanceItem);

      if (date && !attendanceDates.includes(date)) {
        attendanceDates.push(date);
      }
    });
  });

  return attendanceDates.map((date) => ({
    id: `attendance-${date}`,
    label: formatAttendanceDateLabel(date),
    width: 10,
    getValue: (item) => {
      const attendanceRecord = getAttendanceItems(item).find(
        (attendanceItem) => getAttendanceDate(attendanceItem) === date
      );

      return attendanceRecord ? getAttendanceStatus(attendanceRecord) : "-";
    }
  }));
};

// Generate table columns dynamically
const getTableColumns = (rows = [], selectedMonth = null) => {
  const baseColumns = [
    {
      id: "employeeName",
      label: "Employee Name",
      width: 20,
      getValue: (item) => getFirstFieldValue(item, [
        "EmployeeName",
        "employeeName",
        "Name",
        "name",
        "employee_name",
        "UserName",
        "userName"
      ])
    },
    {
      id: "month",
      label: "Month",
      width: 15,
      getValue: (item) => getFirstFieldValue(item, [
        "Month",
        "month",
        "MonthName",
        "monthName",
        "month_name",
        "AttendanceMonth",
        "attendanceMonth"
      ], getSelectedMonthLabel(selectedMonth))
    },
    {
      id: "location",
      label: "Location",
      width: 15,
      getValue: (item) => getFirstFieldValue(item, [
        "LocationName",
        "locationName",
        "Location",
        "location",
        "location_name"
      ])
    },
    {
      id: "departmentName",
      label: "Department Name",
      width: 20,
      getValue: (item) => getFirstFieldValue(item, [
        "DepartmentName",
        "departmentName",
        "Department",
        "department",
        "department_name"
      ])
    }
  ];

  const attendanceColumns = generateAttendanceColumns(rows);
  
  // Add summary columns
  const summaryColumns = [
    {
      id: "totalPresent",
      label: "Present",
      width: 15,
      getValue: (item) => getFirstFieldValue(item, [
        "TotalPresent",
        "totalPresent",
        "total_present",
        "PresentDays",
        "presentDays",
        "present_days",
        "Present",
        "present"
      ])
    },
    {
      id: "totalAbsent",
      label: "Absent",
      width: 15,
      getValue: (item) => getFirstFieldValue(item, [
        "TotalAbsent",
        "totalAbsent",
        "total_absent",
        "AbsentDays",
        "absentDays",
        "absent_days",
        "Absent",
        "absent"
      ])
    },
    {
      id: "leave",
      label: "Leave",
      width: 12,
      getValue: () => ""
    },
    {
      id: "wo",
      label: "WO",
      width: 12,
      getValue: () => ""
    },
    {
      id: "holiday",
      label: "Holiday",
      width: 12,
      getValue: () => ""
    },
    {
      id: "attendancePercentage",
      label: "Attendance Percentage",
      width: 20,
      getValue: (item) => formatAttendancePercentage(item)
    }
  ];

  return [...baseColumns, ...attendanceColumns, ...summaryColumns];
};

const getFirstFieldValue = (item, fieldNames, fallback = EMPTY_CELL_VALUE) => {
  const value = fieldNames
    .map((fieldName) => item?.[fieldName])
    .find((fieldValue) => (
      fieldValue !== null &&
      fieldValue !== undefined &&
      String(fieldValue).trim() !== ""
    ));

  return value === undefined ? fallback : value;
};

const getNumberFieldValue = (item, fieldNames) => {
  const value = getFirstFieldValue(item, fieldNames, "");
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatAttendancePercentage = (item) => {
  const percentage = getFirstFieldValue(
    item,
    [
      "AttendancePercentage",
      "attendancePercentage",
      "attendance_percentage",
      "AttendancePercent",
      "attendancePercent",
      "attendance_percent",
      "Percentage",
      "percentage"
    ],
    ""
  );

  if (percentage !== "") {
    return percentage;
  }

  const totalDays = getNumberFieldValue(item, [
    "TotalDays",
    "totalDays",
    "total_days",
    "Total",
    "total"
  ]);
  const presentDays = getNumberFieldValue(item, [
    "TotalPresent",
    "totalPresent",
    "total_present",
    "PresentDays",
    "presentDays",
    "present_days",
    "Present",
    "present"
  ]);

  if (totalDays && presentDays !== null) {
    return `${((presentDays / totalDays) * 100).toFixed(2)}%`;
  }

  return EMPTY_CELL_VALUE;
};

const MusterRoll = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [visibleRowCount, setVisibleRowCount] = useState(INITIAL_VISIBLE_ROWS);
  const [apiData, setApiData] = useState(null);
  const [locationNames, setLocationNames] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [departmentError, setDepartmentError] = useState('');
  const departmentRequestIdRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [locationError, setLocationError] = useState(false);
  
  // State for Month and Year
  const [selectedMonth, setSelectedMonth] = useState();
  const [selectedYear, setSelectedYear] = useState();
  const [monthError, setMonthError] = useState(false);
  const [yearError, setYearError] = useState(false);
  
  // Dialog state
  const [absentDialogOpen, setAbsentDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [absentRecords, setAbsentRecords] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  
  // Generate year options (current, next, next to next)
  const currentYear = dayjs().year();
  const yearOptions = [currentYear, currentYear + 1, currentYear + 2];
  
  // Generate month options
  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  // Get table columns
  const attendanceTableColumns = getTableColumns(filteredData, selectedMonth);

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    setMonthError(false);
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    setYearError(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      const apiUrl = getURL("employee-shifts-info/location-names?status=1");
      const AUTH_TOKEN = `${localStorage.getItem('token_type')} ${localStorage.getItem('access_token')}`;
      
      try {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: AUTH_TOKEN,
          },
        });

        const extractedLocationNames = response.data.map(item => item.LocationName);
        setLocationNames(extractedLocationNames);
        setApiData(response.data);
      } catch (error) {
        console.error('Error:', error);
      }
    };
  
    fetchData();
  }, []);

  const updateLocation = useCallback((value) => {
    setSelectedLocation(value);
    const selectedLocationObject = apiData?.find((location) => location.LocationName === value);
    const locationId = selectedLocationObject ? selectedLocationObject.Id : null;
    setSelectedLocationId(locationId);
    if (value) {
      localStorage.setItem("NetLocation", value);
    } else {
      localStorage.removeItem("NetLocation");
    }
    setLocationError(false);
    
    setSelectedDepartments([]);
    setDepartmentOptions([]);
    setDepartmentError('');

    const requestId = departmentRequestIdRef.current + 1;
    departmentRequestIdRef.current = requestId;

    if (!locationId) {
      setDepartmentLoading(false);
      return;
    }

    setDepartmentLoading(true);
    getDepartmentsByLocation({ location_id: locationId }, (response) => {
      if (departmentRequestIdRef.current !== requestId) return;

      if (response.status === 200) {
        setDepartmentOptions(normalizeDepartmentOptions(response.data));
        setDepartmentError('');
      } else {
        setDepartmentOptions([]);
        setDepartmentError('Unable to load departments');
      }
      setDepartmentLoading(false);
    });
  }, [apiData]);

  useEffect(() => {
    const savedLocation = localStorage.getItem("NetLocation");
    // if (savedLocation && apiData) {
    //   updateLocation(savedLocation);
    // }
  }, [apiData, updateLocation]);

  const handleLocationChange = (value) => {
    updateLocation(value);
  };

  const handleDepartmentChange = (_event, value) => {
    if (!selectedLocation || departmentLoading) {
      setSelectedDepartments([]);
      return;
    }
    if (value.some((option) => option.isSelectAll)) {
      handleSelectAllDepartments();
      return;
    }
    setSelectedDepartments(value);
  };

  const handleSelectAllDepartments = () => {
    if (!departmentOptions.length) {
      setSelectedDepartments([]);
      return;
    }
    if (selectedDepartments.length === departmentOptions.length) {
      setSelectedDepartments([]);
    } else {
      setSelectedDepartments(departmentOptions);
    }
  };

  const handleSubmit = () => {
    const locationId = selectedLocationId || 0;

    setLoading(true);
    setLocationError(false);
    setMonthError(false);
    setYearError(false);

    if (locationId === 0 || !selectedMonth || !selectedYear) {
      if (locationId === 0) {
        setLocationError(true);
      }
      if (!selectedMonth) {
        setMonthError(true);
      }
      if (!selectedYear) {
        setYearError(true);
      }
      setLoading(false);
      return;
    }

    // Get department IDs for the selected departments
    const departmentIds = selectedDepartments.map(dept => dept.Id);
    const departmentNames = selectedDepartments.map(dept => dept.DepartmentName);

    const data = {
      location_id: locationId,
      month: selectedMonth.value || selectedMonth,
      year: selectedYear,
      department_id: departmentIds.join(','),
    };
    
    console.log('Submitting with data:', data);
    
    getEmployeeMusterRoll(data, (response) => {
      if (response.status === 200) {
        setFilteredData(response.data);
        setVisibleRowCount(INITIAL_VISIBLE_ROWS);
      }
      setLoading(false);
    });
  };

  const handleExportExcel = async () => {
    const exportData = filteredData;
    const monthName = getSelectedMonthLabel(selectedMonth);
    const fileName = `Muster_Roll_${monthName}_${selectedYear}.xlsx`;

    if (exportData.length !== 0) {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Muster Roll");
      
      // Define headers with styles
      worksheet.columns = attendanceTableColumns.map((column) => ({
        header: column.label,
        key: column.id,
        width: column.width,
        alignment: { horizontal: "center" },
      }));

      // Add rows with data
      exportData.forEach((item, index) => {
        const rowData = attendanceTableColumns.reduce((acc, column) => ({
          ...acc,
          [column.id]: column.getValue(item, index)
        }), {});
        const row = worksheet.addRow(rowData);

        row.eachCell((cell) => {
          cell.alignment = { horizontal: "center" };
        });
      });

      // Apply styles to headers
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center" };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
    } else {
      alert("No data to export.");
    }
  };

  // Handle click on Absent Days
  const handleAbsentDaysClick = async (item) => {
    const employeeName = getFirstFieldValue(item, [
      "EmployeeName",
      "employeeName",
      "Name",
      "name",
      "employee_name",
      "UserName",
      "userName"
    ]);

    if (!employeeName || employeeName === EMPTY_CELL_VALUE) {
      alert("Employee name not found");
      return;
    }

    setSelectedEmployee(item);
    setAbsentDialogOpen(true);
    setDialogLoading(true);

    try {
      // Call mock API
      const records = await mockAbsentDaysAPI(employeeName);
      setAbsentRecords(records);
    } catch (error) {
      console.error("Error fetching absent days:", error);
      setAbsentRecords([]);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setAbsentDialogOpen(false);
    setSelectedEmployee(null);
    setAbsentRecords([]);
  };

  const visibleData = filteredData.slice(0, visibleRowCount);

  const handleTableScroll = useCallback((event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight <= SCROLL_LOAD_THRESHOLD_PX;

    if (!isNearBottom) {
      return;
    }

    setVisibleRowCount((currentCount) => (
      Math.min(currentCount + ROW_LOAD_BATCH_SIZE, filteredData.length)
    ));
  }, [filteredData.length]);

  const errorHelperSx = {
    minHeight: 20,
    mt: 0.5,
  };

  const filterControlSx = (hasError = false, width = FILTER_CONTROL_WIDTH) => ({
    width,
    minWidth: width,
    '& .MuiOutlinedInput-root': {
      minHeight: 56,
      backgroundColor: 'background.paper',
      '& fieldset': {
        borderColor: hasError ? 'error.main' : OUTLINED_BORDER_COLOR,
      },
      '&:hover fieldset': {
        borderColor: hasError ? 'error.main' : 'text.primary',
      },
      '&.Mui-focused fieldset': {
        borderColor: hasError ? 'error.main' : 'primary.main',
      },
      '&.Mui-disabled fieldset': {
        borderColor: hasError ? 'error.main' : OUTLINED_BORDER_COLOR,
      },
    },
    '& .MuiFormHelperText-root': {
      minHeight: 20,
      marginLeft: 0,
      marginRight: 0,
    },
    '& .MuiInputLabel-root': {
      backgroundColor: 'background.paper',
      px: 0.5,
    },
  });

  return (
    <Page title="Muster Roll Report">
      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              flexWrap: 'nowrap',
              gap: 2,
              mx: 2,
              mt: 2,
              mb: 2,
              overflowX: 'auto',
              pt: 1.5,
              pb: 1,
            }}
          >
            {/* Year Dropdown */}
            <FormControl sx={filterControlSx(yearError)}>
              <Autocomplete
                value={selectedYear}
                onChange={(_event, value) => handleYearChange(value || null)}
                options={yearOptions}
                getOptionLabel={(option) => option?.toString() || ""}
                isOptionEqualToValue={(option, value) => option === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Year"
                    variant="outlined"
                    error={yearError}
                    helperText={yearError ? "Please select Year" : " "}
                  />
                )}
                sx={filterControlSx(yearError)}
              />
            </FormControl>

            {/* Month Dropdown */}
            <FormControl sx={filterControlSx(monthError)}>
              <Autocomplete
                value={selectedMonth}
                onChange={(_event, value) => handleMonthChange(value || null)}
                options={monthOptions}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option.value === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Month"
                    variant="outlined"
                    error={monthError}
                    helperText={monthError ? "Please select Month" : " "}
                  />
                )}
                sx={filterControlSx(monthError)}
              />
            </FormControl>

            {/* Location Dropdown */}
            <FormControl sx={filterControlSx(locationError)}>
              <Autocomplete
                value={selectedLocation}
                onChange={(_event, value) => handleLocationChange(value || "")}
                options={locationNames}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location"
                    variant="outlined"
                    error={locationError}
                    helperText={locationError ? "Please Select Location" : " "}
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                getOptionLabel={(option) => option || ""}
                noOptionsText="No locations available"
                sx={filterControlSx(locationError)}
              />
            </FormControl>

            {/* Department Dropdown */}
            <FormControl sx={filterControlSx(Boolean(departmentError))}>
              <Autocomplete
                multiple
                limitTags={1}
                renderTags={() => null}
                value={selectedDepartments}
                onChange={handleDepartmentChange}
                options={departmentOptions}
                loading={departmentLoading}
                loadingText="Loading departments..."
                noOptionsText={
                  selectedLocation
                    ? (departmentError || "No departments available")
                    : "Select location first"
                }
                getOptionLabel={(option) => option.DepartmentName || ""}
                isOptionEqualToValue={(option, value) => String(option.Id) === String(value.Id)}
                disabled={!selectedLocation || departmentLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    variant="outlined"
                    error={Boolean(departmentError)}
                    helperText={departmentError || " "}
                    placeholder={
                      departmentLoading
                        ? "Loading departments..."
                        : !selectedLocation
                          ? "Select location first"
                          : "Select departments"
                    }
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <MenuItem {...props}>
                    <Checkbox
                      checked={
                        option.isSelectAll
                          ? departmentOptions.length > 0 && selectedDepartments.length === departmentOptions.length
                          : selected
                      }
                    />
                    <ListItemText primary={option.DepartmentName} />
                  </MenuItem>
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 200,
                  },
                }}
                componentsProps={{
                  popper: {
                    placement: 'bottom-start',
                  },
                }}
                sx={filterControlSx(Boolean(departmentError))}
                filterOptions={(options) => {
                  if (!selectedLocation || departmentLoading || options.length === 0) {
                    return options;
                  }
                  const selectAllOption = {
                    Id: -1,
                    DepartmentName: 'Select All',
                    isSelectAll: true,
                  };
                  return [selectAllOption, ...options];
                }}
              />
            </FormControl>

            {/* Submit and Export Buttons */}
            <Box sx={{ display: 'flex', gap: 1, minHeight: 56, alignItems: 'center', pt: 0 }}>
              <LoadingButton type="submit" variant="contained" onClick={handleSubmit} loading={loading}>
                Submit
              </LoadingButton>

              <LoadingButton type="submit" variant="contained" onClick={handleExportExcel}>
                Export
              </LoadingButton>
            </Box>
          </Box>

          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              onScroll={handleTableScroll}
              sx={{ minWidth: 1100, maxHeight: '65vh', overflow: 'auto' }}
            >
              <Table stickyHeader sx={{ minWidth: 1100 }}>
                <TableHead>
	                  <TableRow>
	                    {attendanceTableColumns.map((column) => (
	                      <TableCell key={column.id} sx={{ whiteSpace: 'nowrap' }}>{column.label}</TableCell>
	                    ))}
	                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleData.map((item, index) => {
                    const absoluteIndex = index;

                    return (
                      <TableRow key={absoluteIndex}>
                        {attendanceTableColumns.map((column) => {
                          const value = column.getValue(item, absoluteIndex);
                          
                          return (
                            <TableCell key={column.id}>
                              {value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {filteredData.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
              Showing {visibleData.length} of {filteredData.length}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Absent Days Dialog */}
      <Dialog
        open={absentDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Absent Days Details
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {selectedEmployee && (
            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
              {getFirstFieldValue(selectedEmployee, [
                "EmployeeName",
                "employeeName",
                "Name",
                "name",
                "employee_name",
                "UserName",
                "userName"
              ])}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {dialogLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography>Loading absent records...</Typography>
            </Box>
          ) : absentRecords.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Employee Name</strong></TableCell>
                    <TableCell><strong>Location</strong></TableCell>
                    <TableCell><strong>Department Name</strong></TableCell>
                    <TableCell><strong>Leave Type</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {absentRecords.map((record, index) => (
                    <TableRow key={index}>
                      <TableCell>{record.employeeName}</TableCell>
                      <TableCell>{record.location}</TableCell>
                      <TableCell>{record.departmentName}</TableCell>
                      <TableCell>
                        <Chip 
                          label={record.leaveType} 
                          size="small"
                          color={
                            record.leaveType === "Sick Leave" ? "error" :
                            record.leaveType === "Casual Leave" ? "warning" :
                            "default"
                          }
                        />
                      </TableCell>
                      <TableCell>{record.date}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography>No absent records found for this employee.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
};

export default MusterRoll;
